import { Card, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import React, { type ComponentProps } from "react";
import { type Input, type MarketDataBrowser } from "~/components/market-data/MarketDataBrowser";
import { Icon } from "~/components/ui/core/Icon";
import { JobSelector } from "~/components/ui/JobSelector";
import { LegacyCompanyFilters } from "~/components/ui/LegacyCompanyFilters";
import { LocationSelector } from "~/components/ui/LocationSelector";
import { useI18n } from "~/lib/i18n/useI18n";

type CompareFiltersCardProps = {
  company: ComponentProps<typeof MarketDataBrowser>["company"];
  input: Input;
  readOnly: boolean;
  onChange: (input: Partial<Input>) => void;
};

export const CompareFiltersCard: React.FC<CompareFiltersCardProps> = ({ company, input, readOnly, onChange }) => {
  const { t } = useI18n();

  return (
    <Card>
      <Stack spacing={3}>
        <Typography textTransform="capitalize" fontWeight="500" color="gray.600">
          {t("components.market-data-filters.compare-multiple-jobs")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={input.compare.enabled}
              color="info"
              size="small"
              disabled={readOnly}
              onChange={(event) => {
                onChange({
                  ...input,
                  compare: {
                    ...input.compare,
                    enabled: event.target.checked,
                  },
                });
              }}
            />
          }
          className="flex w-full justify-between"
          labelPlacement="start"
          label={<Typography variant="subtitle2">{t("components.market-data-filters.compare-jobs")}</Typography>}
        />

        {!input.compare.enabled && (
          <div className="flex items-start space-x-2">
            <Icon className="text-gray-500" name="information" />
            <span className="flex-1 text-xs text-gray-500">
              {t("components.market-data-filters.compare-2-sets-of-jobs")}
            </span>
          </div>
        )}

        {input.compare.enabled && (
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">{t("components.market-data-filters.job")}</Typography>
              <JobSelector
                className="w-full"
                enableCustomJobs
                disabled={readOnly}
                jobIds={input.compare.jobIds}
                onChange={(jobIds) => {
                  onChange({ ...input, compare: { ...input.compare, jobIds } });
                }}
              />
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2">{t("components.market-data-filters.location")}</Typography>
              <LocationSelector
                multiple
                dense={false}
                disabled={readOnly}
                value={input.compare.locations}
                onChange={(event) => {
                  onChange({
                    ...input,
                    compare: {
                      ...input.compare,
                      locations: event.target.value,
                    },
                  });
                }}
              />
            </Stack>

            <Stack spacing={1}>
              <Typography textTransform="capitalize" fontWeight="500" color="gray.600">
                {t("pages.market-data.market-data-filters.market-filters")}
              </Typography>
              <LegacyCompanyFilters
                company={company}
                disabled={readOnly}
                input={input.compare}
                onChange={(value) => {
                  onChange({ compare: { ...input.compare, ...value } });
                }}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
